import React, { useState } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';

const QuizTaker = ({ title, questions, passingScore, onSubmit, lastResult, allowRetake, submitLabel }) => {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(lastResult || null);
  const [submitError, setSubmitError] = useState('');
  const [showReview, setShowReview] = useState(false);
  const [retaking, setRetaking] = useState(false);

  const allAnswered = questions?.every((q) => !!answers?.[q?.id]);

  const handleSelect = (questionId, optionId) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    const { result: submitted, error } = (await onSubmit(answers)) || {};
    setSubmitting(false);

    if (error) {
      setSubmitError(error?.message || 'Something went wrong submitting. Please try again.');
      return;
    }

    if (submitted) {
      setResult({ ...submitted, answers });
      setRetaking(false);
      setShowReview(false);
    }
  };

  const handleStartRetake = () => {
    setAnswers({});
    setShowReview(false);
    setSubmitError('');
    setRetaking(true);
  };

  const getOptionText = (question, optionId) => {
    return question?.options?.find((o) => o?.id === optionId)?.text || '—';
  };

  const renderForm = () => (
    <div className="bg-card rounded-lg p-6 sm:p-8 space-y-6">
      <div>
        <h3 className="text-lg font-bold text-foreground">{title}</h3>
        {passingScore ? <p className="text-sm text-muted-foreground">Passing score: {passingScore}%</p> : null}
        <p className="text-xs text-muted-foreground mt-1">
          {retaking
            ? 'This is your final retake — your answers are locked in once submitted.'
            : 'This assessment allows a single graded attempt. Answer carefully — you can review your answers afterward.'}
        </p>
      </div>

      {questions?.map((question, index) => (
        <div key={question?.id} className="space-y-3">
          <p className="text-sm font-medium text-foreground">
            {index + 1}. {question?.prompt}
          </p>
          <div className="space-y-2">
            {question?.options?.map((option) => (
              <label
                key={option?.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-smooth ${
                  answers?.[question?.id] === option?.id ? 'border-accent bg-accent/5' : 'border-border hover:bg-muted/50'
                }`}
              >
                <input
                  type="radio"
                  name={question?.id}
                  checked={answers?.[question?.id] === option?.id}
                  onChange={() => handleSelect(question?.id, option?.id)}
                />
                <span className="text-sm text-foreground">{option?.text}</span>
              </label>
            ))}
          </div>
        </div>
      ))}

      {submitError && <p className="text-sm text-destructive text-center">{submitError}</p>}

      <Button variant="default" fullWidth loading={submitting} disabled={!allAnswered} onClick={handleSubmit}>
        {submitLabel || 'Submit'}
      </Button>
      {!allAnswered && <p className="text-xs text-muted-foreground text-center">Answer all questions to submit.</p>}
    </div>
  );

  if (!result || retaking) {
    return renderForm();
  }

  return (
    <div className="bg-card rounded-lg p-6 sm:p-8 space-y-6">
      <div className="text-center">
        <div
          className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            result?.passed ? 'bg-success/10' : 'bg-destructive/10'
          }`}
        >
          <Icon name={result?.passed ? 'CheckCircle2' : 'XCircle'} size={32} className={result?.passed ? 'text-success' : 'text-destructive'} />
        </div>
        <h3 className="text-xl font-bold text-foreground mb-1">{result?.passed ? 'Passed!' : 'Not Quite'}</h3>
        <p className="text-muted-foreground mb-1">
          You scored <span className="font-semibold text-foreground">{result?.score}%</span>
          {passingScore ? ` (passing score: ${passingScore}%)` : ''}
        </p>
        <p className="text-xs text-muted-foreground mb-2">
          This assessment allows a single graded attempt during the course.
        </p>
        {result?.pointsAwarded > 0 && (
          <p className="text-sm text-accent font-medium">+{result?.pointsAwarded} Nesol points earned 🎉</p>
        )}
      </div>

      {submitError && <p className="text-sm text-destructive text-center">{submitError}</p>}

      <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
        <Button
          variant="outline"
          iconName={showReview ? 'ChevronUp' : 'ListChecks'}
          iconPosition="left"
          onClick={() => setShowReview((s) => !s)}
        >
          {showReview ? 'Hide Review' : 'Review Answers'}
        </Button>
        {allowRetake ? (
          <Button variant="default" iconName="RotateCcw" iconPosition="left" onClick={handleStartRetake}>
            Retake
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">Retakes unlock once you finish the whole course.</span>
        )}
      </div>

      {showReview && (
        <div className="space-y-4 pt-4 border-t border-border">
          {questions?.map((question, index) => {
            const yourAnswerId = result?.answers?.[question?.id];
            const isCorrect = yourAnswerId === question?.correctOptionId;
            return (
              <div key={question?.id} className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  {index + 1}. {question?.prompt}
                </p>
                <p className={`text-sm ${isCorrect ? 'text-success' : 'text-destructive'}`}>
                  Your answer: {getOptionText(question, yourAnswerId)}
                </p>
                {!isCorrect && (
                  <p className="text-sm text-success">
                    Correct answer: {getOptionText(question, question?.correctOptionId)}
                  </p>
                )}
                {!isCorrect && question?.resourceHint && (
                  <p className="text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2 mt-1">
                    💡 {question?.resourceHint}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default QuizTaker;